import { useCallback, useEffect, useRef, useState } from 'react';

import { verifySentPoints } from 'services/actionlog.service';
import { sendPreferenceNotification } from 'services/dashboard.service';
import { User } from 'types/user';

import { RecognitionAlertContent } from './recognition';

interface ColleagueChecksInput {
  target?: User;
  /** Dados de quem envia (`find/info`): a matrícula e os relacionamentos cadastrados. */
  sender: any;
  userId?: string;
  /** HTML da mensagem enquanto a revisão está na tela; fora dela, `undefined`. */
  messageInReview?: string;
  /** O colega não pode ser reconhecido. Precisa ser estável: é dependência dos efeitos. */
  onRefused: (reason: RecognitionAlertContent) => void;
  /** Esta mensagem já foi enviada a este colega na fase. Precisa ser estável, pelo mesmo motivo. */
  onMessageAlreadySent: (reason: RecognitionAlertContent) => void;
}

type SentToColleague = { targetId: string; total: number; notice: string };

/** O grupo restrito é o único bloqueio que traz o aviso formatado (`message_html`). */
function groupBlock(answer: any): RecognitionAlertContent | undefined {
  return answer?.group_blocked ? { text: answer.message, html: answer.message_html } : undefined;
}

/**
 * O que impede ou limita o reconhecimento do colega escolhido: a renúncia dele, o relacionamento
 * cadastrado com quem envia e o que o servidor já sabe (grupo restrito, limite por pessoa, pontos
 * já enviados, mensagem repetida). Antecipa o que o servidor recusaria no envio, para ninguém
 * escrever o reconhecimento à toa. Quem decide é sempre o servidor: se uma conferência falhar, o
 * fluxo segue e o envio é recusado lá.
 */
export function useColleagueChecks({
  target,
  sender,
  userId,
  messageInReview,
  onRefused,
  onMessageAlreadySent,
}: ColleagueChecksInput) {
  const [sentToColleague, setSentToColleague] = useState<SentToColleague>();
  const [askingRelationship, setAskingRelationship] = useState(false);

  /** Colega já conferido no servidor: a conferência roda uma vez por pessoa escolhida. */
  const verifiedTargetRef = useRef<string>();
  /** Colega para quem a confirmação de relacionamento já foi pedida. */
  const relationshipAskedRef = useRef<string>();
  /** Quem já foi avisado de que tentaram reconhecê-lo apesar da renúncia. */
  const optOutNotifiedRef = useRef<string[]>([]);

  // Colega escolhido (na busca ou vindo do rascunho): renúncia e relacionamento.
  useEffect(() => {
    if (!target) {
      relationshipAskedRef.current = undefined;
      return;
    }

    if (target.extra?.recognition === false) {
      if (!optOutNotifiedRef.current.includes(target._id)) {
        optOutNotifiedRef.current.push(target._id);
        sendPreferenceNotification({
          target: target._id,
          user: userId,
          item: 'recognition',
          created: { $date: new Date().getTime() },
        });
      }

      onRefused({
        text: `${target.extra?.nome_guerra} optou, em "Minhas Preferências", pela não participação nesta dinâmica.`,
      });
      return;
    }

    const hasRelationship = Array.isArray(sender?.relacionamentos) && sender.relacionamentos.includes(target.extra?.id);

    if (hasRelationship && relationshipAskedRef.current !== target._id) {
      relationshipAskedRef.current = target._id;
      setAskingRelationship(true);
    }
  }, [target, sender, userId, onRefused]);

  // Conferência do colega no servidor, uma vez por pessoa escolhida.
  useEffect(() => {
    if (!target) {
      verifiedTargetRef.current = undefined;
      setSentToColleague(undefined);
      return;
    }

    if (!sender?._id || verifiedTargetRef.current === target._id) return;

    const targetId = target._id;

    verifiedTargetRef.current = targetId;

    verifySentPoints(sender._id, targetId).then(answer => {
      if (verifiedTargetRef.current !== targetId) return;

      const blocked = groupBlock(answer);

      if (blocked) {
        onRefused(blocked);
        return;
      }

      if (answer?.limit_reached) {
        onRefused({ text: answer.message });
        return;
      }

      const total = answer?.points_sent_total > 0 ? answer.points_sent_total : 0;

      setSentToColleague({ targetId, total, notice: total > 0 ? answer.message : '' });
    });
  }, [target, sender, onRefused]);

  // Na revisão, o servidor confere se esta mesma mensagem já foi enviada a esta pessoa na fase.
  useEffect(() => {
    if (!target || !sender?._id || !messageInReview) return;

    let stale = false;

    verifySentPoints(sender._id, target._id, messageInReview).then(answer => {
      if (stale) return;

      const blocked = groupBlock(answer);

      if (blocked) {
        onRefused(blocked);
        return;
      }

      if (answer?.message_already_sent) onMessageAlreadySent({ text: answer.message });
    });

    return () => {
      stale = true;
    };
  }, [target, sender, messageInReview, onRefused, onMessageAlreadySent]);

  // O total vale só para o colega conferido: trocado o colega, volta a zero até a resposta dele.
  const sentToTarget = sentToColleague?.targetId === target?._id ? sentToColleague : undefined;

  return {
    /** Pontos já enviados a este colega na fase, segundo o servidor. */
    pointsSent: sentToTarget?.total ?? 0,
    /** Aviso do servidor sobre os pontos já enviados, mostrado ao chegar na etapa dos pontos. */
    sentPointsNotice: sentToTarget?.notice ?? '',
    askingRelationship,
    closeRelationshipQuestion: useCallback(() => setAskingRelationship(false), []),
  };
}
